import { Box, FileText, Users, Truck, CreditCard } from 'lucide-react';

export const GenericImporterImportTypes = {
    ARTICLES: {
        type: 'articles',
        title: 'Importera artiklar',
        subtitle: 'Ladda upp en CSV-fil med artiklar för att importera till systemet',
        apiEndpoint: '/articles/import',
        icon: Box,
        requiredFields: ['unitprice', 'type', 'unit', 'tax_rate', 'description'],
        fieldsConfig: {
            productcode: {
                label: "Produktkod",
                required: false
            },
            description: {
                label: "Beskrivning",
                validate: (value) => value?.length > 150 ? "Beskrivning för lång (max 150 tecken)" : null
            },
            unitprice: {
                label: "Enhetspris",
                required: true,
                validate: (value) => {
                    if (!value) return "Enhetspris är obligatoriskt";
                    if (isNaN(value) || parseFloat(value) < 0) return "Ogiltigt enhetspris";
                    return null;
                }
            },
            type: {
                label: "Typ",
                options: ['Goods', 'Service'],
                displayOptions: ['Varor (Goods)', 'Tjänst (Service)'],
                validate: (value) => {
                    if (value && !['Goods', 'Service'].includes(value)) {
                        return "Ogiltig typ";
                    }
                    return null;
                }
            },
            unit: {
                label: "Enhet", // Keep Swedish label for UI display
                options: ['Piece', 'Hour', 'Liter', 'Kilogram', 'Square meter',
                    'Cubic meter', 'Meter', 'Package', 'Set', 'Day', 'Month'],
                displayOptions: ['Styck (Piece)', 'Timme (Hour)', 'Liter', 'Kilogram', 'Kvadratmeter (Square meter)',
                    'Kubikmeter (Cubic meter)', 'Meter', 'Förpackning (Package)', 'Set', 'Dag (Day)', 'Månad (Month)'],
                validate: (value) => {
                    if (value && !['Piece', 'Hour', 'Liter', 'Kilogram', 'Square meter',
                        'Cubic meter', 'Meter', 'Package', 'Set', 'Day', 'Month'].includes(value)) {
                        return "Ogiltig enhet";
                    }
                    return null;
                }
            },
            tax_rate: {
                label: "Momssats",
                options: ['0%', '6%', '12%', '25%'],
                validate: (value) => {
                    if (value && !['0%', '6%', '12%', '25%'].includes(value)) {
                        return "Ogiltig momssats";
                    }
                    return null;
                }
            }
        }
    },

    CUSTOMERS: {
        type: 'customers',
        title: 'Importera kunder',
        subtitle: 'Ladda upp en CSV-fil med kunder för att importera till systemet',
        apiEndpoint: '/sales/customers/import',
        icon: Users,
        requiredFields: ['name'],
        fieldsConfig: {
            name: {
                label: "Namn",
                required: true,
                validate: (value) => !value ? "Namn är obligatoriskt" : null
            },
            type: {
                label: "Typ",
                options: ['company', 'individual'],
                displayOptions: ['Företag (Company)', 'Privatperson (Individual)'],
                defaultValue: 'company',
                validate: (value) => {
                    if (value && !['company', 'individual'].includes(value.toLowerCase())) {
                        return "Ogiltig typ";
                    }
                    return null;
                },
                normalize: (value) => {
                    if (!value) return 'company';
                    const lower = value.toLowerCase();
                    if (lower === 'company' || lower === 'företag') return 'company';
                    if (lower === 'individual' || lower === 'privatperson') return 'individual';
                    return lower;
                }
            },
            orgnumber: {
                label: "Organisationsnummer",
                validate: (value) => {
                    if (value && value.length > 45) {
                        return "Organisationsnummer är för långt (max 45 tecken)";
                    }
                    return null;
                },
                normalize: (value) => {
                    if (!value) return '';
                    // Remove dashes and spaces for consistent storage
                    return value.toString().replace(/[-\s]/g, '');
                }
            },
            vatnumber: {
                label: "Momsreg.nr.",
                validate: (value) => {
                    if (value && value.length > 45) {
                        return "Momsregistreringsnummer är för långt (max 45 tecken)";
                    }
                    return null;
                }
            },
            email: {
                label: "E-post",
                required: false,
                validate: (value) => {
                    if (value) {
                        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        if (!emailRegex.test(value)) return "Ogiltig e-postadress";
                    }
                    return null;
                }
            },
            phone: {
                label: "Telefon",
                validate: (value) => {
                    if (value && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/.test(value)) {
                        return "Ogiltigt telefonnummer";
                    }
                    return null;
                }
            },
            street1: {
                label: "Adress 1",
                validate: (value) => {
                    if (value && value.length > 45) {
                        return "Adress är för lång (max 45 tecken)";
                    }
                    return null;
                }
            },
            street2: {
                label: "Adress 2",
                validate: (value) => {
                    if (value && value.length > 45) {
                        return "Adress är för lång (max 45 tecken)";
                    }
                    return null;
                }
            },
            postalcode: {
                label: "Postnummer",
                validate: (value) => {
                    if (value) {
                        // Normalize: remove spaces and common prefixes like "SE-"
                        const normalized = value.toString().replace(/^SE-?/i, '').replace(/\s/g, '');
                        if (!/^[0-9]{5}$/.test(normalized)) {
                            return "Ogiltigt postnummer (5 siffror)";
                        }
                    }
                    return null;
                },
                normalize: (value) => {
                    if (!value) return '';
                    return value.toString().replace(/^SE-?/i, '').replace(/\s/g, '');
                }
            },
            city: {
                label: "Stad"
            },
            country: {
                label: "Land",
                defaultValue: "Sverige"
            },
            payment_terms: {
                label: "Betalningsvillkor (dagar)",
                validate: (value) => {
                    if (value) {
                        const terms = parseInt(value);
                        if (isNaN(terms) || terms < 0) {
                            return "Betalningsvillkor måste vara ett positivt tal";
                        }
                    }
                    return null;
                },
                normalize: (value) => {
                    if (!value) return '';
                    const parsed = parseInt(value);
                    return isNaN(parsed) ? '' : parsed.toString();
                }
            }
        }
    },

    SUPPLIERS: {
        type: 'suppliers',
        title: 'Importera leverantörer',
        subtitle: 'Ladda upp en CSV-fil med leverantörer för att importera till systemet',
        apiEndpoint: '/suppliers/import',
        icon: Truck,
        requiredFields: ['name'],
        fieldsConfig: {
            name: {
                label: "Namn",
                required: true,
                validate: (value) => !value ? "Namn är obligatoriskt" : null
            },
            email: {
                label: "E-post",
                validate: (value) => {
                    if (value) {
                        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        if (!emailRegex.test(value)) return "Ogiltig e-postadress";
                    }
                    return null;
                }
            },
            phone: {
                label: "Telefon",
                validate: (value) => {
                    if (value && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/.test(value)) {
                        return "Ogiltigt telefonnummer";
                    }
                    return null;
                }
            },
            address: {
                label: "Adress"
            },
            postal_code: {
                label: "Postnummer",
                validate: (value) => {
                    if (value) {
                        const normalized = value.toString().replace(/^SE-?/i, '').replace(/\s/g, '');
                        if (!/^[0-9]{5}$/.test(normalized)) {
                            return "Ogiltigt postnummer (5 siffror)";
                        }
                    }
                    return null;
                },
                normalize: (value) => {
                    if (!value) return '';
                    return value.toString().replace(/^SE-?/i, '').replace(/\s/g, '');
                }
            },
            city: {
                label: "Stad"
            },
            country: {
                label: "Land",
                defaultValue: "Sverige"
            },
            vat_number: {
                label: "Momsreg.nr."
            }
        }
    },

    TRANSACTIONS: {
        type: 'transactions',
        title: 'Importera transaktioner',
        subtitle: 'Ladda upp en CSV-fil med transaktioner för att importera till systemet',
        apiEndpoint: '/transactions/import',
        icon: CreditCard,
        requiredFields: ['date', 'description', 'amount'],
        fieldsConfig: {
            date: {
                label: "Datum",
                required: true,
                validate: (value) => {
                    if (!value) return "Datum är obligatoriskt";
                    // Basic date validation (YYYY-MM-DD)
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Ogiltigt datumformat (YYYY-MM-DD)";
                    return null;
                }
            },
            description: {
                label: "Beskrivning",
                required: true,
                validate: (value) => !value ? "Beskrivning är obligatoriskt" : null
            },
            amount: {
                label: "Belopp",
                required: true,
                validate: (value) => {
                    if (!value) return "Belopp är obligatoriskt";
                    if (isNaN(value)) return "Ogiltigt belopp";
                    return null;
                }
            },
            type: {
                label: "Typ",
                options: ['Intäkt', 'Kostnad'],
                validate: (value) => {
                    if (value && !['Intäkt', 'Kostnad'].includes(value)) {
                        return "Ogiltig typ";
                    }
                    return null;
                }
            },
            category: {
                label: "Kategori"
            }
        }
    }
};

export default GenericImporterImportTypes;